// prism/frontend/src/api/client.js
// Shared API client with caching and request deduplication

import { API_BASE_URL } from '../config';

export const API_BASE = API_BASE_URL;

// ============================================================================
// API CACHING & REQUEST DEDUPLICATION
// ============================================================================
const API_CACHE = new Map();
const IN_FLIGHT_REQUESTS = new Map();

// Cache configuration (in milliseconds)
export const CACHE_CONFIG = {
  protocols: 60000,      // 60 seconds
  workflows: 120000,     // 120 seconds (less frequent changes)
  search: 300000,        // 5 minutes
  user: 600000,          // 10 minutes
};

export function getCacheKey(endpoint, params = {}) {
  const paramStr = Object.entries(params)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${v}`)
    .join('&');
  return paramStr ? `${endpoint}?${paramStr}` : endpoint;
}

export function getFromCache(key) {
  const cached = API_CACHE.get(key);
  if (!cached) return null;
  
  const now = Date.now();
  if (now - cached.timestamp > cached.ttl) {
    API_CACHE.delete(key);
    return null;
  }
  
  return cached.data;
}

export function setInCache(key, data, ttl) {
  API_CACHE.set(key, {
    data,
    timestamp: Date.now(),
    ttl,
  });
}

export async function fetchWithDedup(cacheKey, fetcher, ttl) {
  try {
    // Return cached data if available
    const cached = getFromCache(cacheKey);
    if (cached) {
      return cached;
    }
    
    // Return in-flight request if one exists (deduplication)
    if (IN_FLIGHT_REQUESTS.has(cacheKey)) {
      return IN_FLIGHT_REQUESTS.get(cacheKey);
    }
    
    // Create new request
    const requestPromise = fetcher()
      .then(data => {
        setInCache(cacheKey, data, ttl);
        IN_FLIGHT_REQUESTS.delete(cacheKey);
        return data;
      })
      .catch(err => {
        IN_FLIGHT_REQUESTS.delete(cacheKey);
        throw err;
      });
    
    // Store in-flight request
    IN_FLIGHT_REQUESTS.set(cacheKey, requestPromise);
    return requestPromise;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// Get auth token from localStorage
export function getAuthHeaders() {
  const token = localStorage.getItem("prism_token");
  return {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

// Utility function to manually invalidate cache
export function invalidateCache(key) {
  if (key === 'all') {
    API_CACHE.clear();
  } else {
    API_CACHE.delete(key);
  }
}

// Utility function to get cache stats
export function getCacheStats() {
  return {
    cacheSize: API_CACHE.size,
    inFlightRequests: IN_FLIGHT_REQUESTS.size,
  };
}

// Clear all caches (used by logout and data operations)
export function clearAllCaches() {
  API_CACHE.clear();
  IN_FLIGHT_REQUESTS.clear();
}
